import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

// --- Helper function for data transformation (moved outside for purity) ---
/**
 * Groups a flattened array of gallery items by their 'name' property
 * into a structure suitable for tab rendering.
 * @param {Array<Object>} galleryArray - The array of image items.
 * @returns {Array<Object>} Grouped array: [{ name: 'Group Name', items: [...], total: N }]
 */
const groupByGalleryName = (galleryArray) => {
  const groups = {};

  galleryArray.forEach(item => {
    if (!groups[item.name]) groups[item.name] = [];
    groups[item.name].push(item);
  });

  return Object.entries(groups).map(([name, items]) => ({
    name,
    items,
    total: items.length
  }));
};

const ProjectGallery = () => {
  // --- STATE ---
  const [projectData, setProjectData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [galleryGroups, setGalleryGroups] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // --- CONSTANTS & ROUTING ---
  const { id } = useParams();
  const projectId = id;

  const IMG_BASE_URL = "https://mithahomesfrontend.pemixcels.com";
  // Check if projectId exists before building URL to prevent unnecessary API call on initial render without ID
  const API_URL = projectId ? `${IMG_BASE_URL}/api/mainprojectprocess/projectsgetapi/${projectId}` : null; 

  // --- LIGHTBOX NAVIGATION LOGIC (Memoized for use in useEffect dependency array) ---

  const nextImage = useCallback(() => {
    // Safety check
    if (!galleryGroups.length) return;
    const group = galleryGroups[activeTab];
    if (!group || !group.items.length) return;

    // Circular navigation: (index + 1) % length
    const nextIndex = (currentImageIndex + 1) % group.items.length; 
    setSelectedImage(group.items[nextIndex]);
    setCurrentImageIndex(nextIndex);
  }, [galleryGroups, activeTab, currentImageIndex]);

  const prevImage = useCallback(() => {
    // Safety check
    if (!galleryGroups.length) return;
    const group = galleryGroups[activeTab];
    if (!group || !group.items.length) return;

    // Circular navigation: (index - 1 + length) % length
    const length = group.items.length;
    const prevIndex = (currentImageIndex - 1 + length) % length; 
    setSelectedImage(group.items[prevIndex]);
    setCurrentImageIndex(prevIndex);
  }, [galleryGroups, activeTab, currentImageIndex]);

  // --- MODAL HANDLERS ---
  const closeImageModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    // Restore scrolling on the main page
    document.body.style.overflow = "auto";
  };

  const openImageModal = (item, index) => {
    setSelectedImage(item);
    setCurrentImageIndex(index);
    setIsModalOpen(true);
    // Prevents scrolling on the main page while the modal is open
    document.body.style.overflow = "hidden"; 
  };

  // Close modal only if the backdrop (the dark area) is clicked
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) closeImageModal();
  };

  // --- SIDE EFFECT: DATA FETCHING ---
  useEffect(() => {
    const fetchProjectData = async () => {
      if (!API_URL) {
        return;
      }

      try {
        const response = await axios.get(API_URL);

        if (response.data.success) {
          const fetchedData = response.data.data;
          setProjectData(fetchedData);

          let flattenedGallery = [];

          if (fetchedData.gallery?.length > 0) {
            fetchedData.gallery.forEach(group => {
              if (Array.isArray(group.images)) {
                group.images.forEach(imagePath => {
                  flattenedGallery.push({
                    name: group.name,
                    image: imagePath,
                  });
                });
              }
            });

            // Group the flattened list by the gallery name
            const grouped = groupByGalleryName(flattenedGallery);
            setGalleryGroups(grouped);
            // Reset active tab if it's out of bounds of the new data
            if (activeTab >= grouped.length) {
                 setActiveTab(0);
            }
          } else {
             setGalleryGroups([]); // Clear groups if no gallery data is found
          }
        } else {
          console.error("Failed to load gallery");
        }
      } catch (err) {
        console.error("Error loading gallery: " + err.message);
      }
    };

    if (projectId) fetchProjectData();
  }, [projectId, API_URL, activeTab]);

  // --- SIDE EFFECT: A11Y Keyboard Controls for Modal ---
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeImageModal();
      } else if (event.key === 'ArrowRight') {
        event.stopPropagation();
        nextImage();
      } else if (event.key === 'ArrowLeft') {
        event.stopPropagation();
        prevImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, nextImage, prevImage]);

  // Check if data loaded but no gallery images exist
  if (!galleryGroups.length || !galleryGroups[activeTab]?.items.length) {
    return null; // Return nothing instead of showing a message
  }

  // --- MAIN COMPONENT RENDER ---
  return (
    <>
      {/* Applying Roboto to the main content container */}
      <div 
        className="gallery-plan-section"
        style={{ fontFamily: 'Roboto, sans-serif' }}
      >
        {/* Title */}
        <h2 style={{
          textAlign: "center",
          fontSize: "clamp(24px, 3vw, 32px)",
          fontWeight: 600,
          color: "#1f2937",
          gap: "80px",
          marginBottom: "5px"
        }}>
Gallery

        </h2>

        {/* TABS */}
        <div style={{ 
          display: "flex", 
          justifyContent: "center", 
          overflowX: "auto", 
          paddingBottom: "10px", 
          marginBottom: "0px" 
        }}>
          <div style={{ 
            display: "flex", 
            gap: "8px",
            flexWrap: "wrap",
            justifyContent: "center"
          }}>
            {galleryGroups.map((group, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                aria-selected={activeTab === i}
                role="tab"
                style={{
                  padding: "12px 24px",
                  fontWeight: 600,
                  border: "1px solid #ffffffff",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.3s",
                  backgroundColor: activeTab === i ? "#e67339" : "#eeeeee",
                  color: activeTab === i ? "#ffffffff" : "#000000ff",
                  fontSize: "16px",
                  fontFamily: 'Roboto, sans-serif' // Ensuring button text uses Roboto
                }}
              
               
              >
                {group.name} 
              </button>
            ))}
          </div>
        </div>

        {/* IMAGE GRID - Using the exact CSS from the design */}
        <div className="gallery-image-lists">
          {galleryGroups[activeTab].items.map((item, index) => (
            <div
              key={index}
              style={{
                backgroundColor: "#f8fafc",
                overflow: "hidden",
                cursor: "pointer",
         
                transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
             
              }}
              onClick={() => openImageModal(item, index)}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <img
                src={IMG_BASE_URL + item.image} 
                style={{ 
                  width: "100%", 
                  height: "230px", // Exact height from CSS
                  objectFit: "cover" // Changed from "fill" to "cover" for better cropping
                }}
                alt={`Gallery image of ${item.name}`}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>

      {/* MODAL (Lightbox) */}
      {isModalOpen && selectedImage && (
        <div
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-label={`Image Viewer: ${selectedImage.name}`}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.95)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
            // Applying Roboto to the modal for font consistency
            fontFamily: 'Roboto, sans-serif'
          }}
        >
          {/* Close Button */}
          <button
            onClick={closeImageModal}
            aria-label="Close image viewer"
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              fontSize: "30px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.3)",
              width: "45px",
              height: "45px",
              borderRadius: "50%",
              color: "#fff",
              cursor: "pointer",
              lineHeight: 1,
              zIndex: 10000,
              transition: "all 0.3s"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.2)";
              e.currentTarget.style.border = "1px solid rgba(255,255,255,0.5)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.border = "1px solid rgba(255,255,255,0.3)";
            }}
          >
            ×
          </button>

          {/* Previous Button */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            aria-label="Previous image"
            style={{
              position: "absolute",
              left: "20px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "30px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "50%",
              width: "45px",
              height: "45px",
              color: "#fff",
              cursor: "pointer",
              lineHeight: 1,
              zIndex: 10000,
              transition: "all 0.3s"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.2)";
              e.currentTarget.style.border = "1px solid rgba(255,255,255,0.5)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.border = "1px solid rgba(255,255,255,0.3)";
            }}
          >
            ‹
          </button>

          {/* Displayed Image */}
          <img
            src={IMG_BASE_URL + selectedImage.image}
            style={{
              maxWidth: "90vw",
              maxHeight: "80vh",
              objectFit: "contain",
              userSelect: "none",
              borderRadius: "4px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)"
            }}
            alt={`Full view of image ${currentImageIndex + 1} in group ${selectedImage.name}`}
          />

          {/* Next Button */}
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            aria-label="Next image"
            style={{
              position: "absolute",
              right: "20px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "30px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "50%",
              width: "45px",
              height: "45px",
              color: "#fff",
              cursor: "pointer",
              lineHeight: 1,
              zIndex: 10000,
              transition: "all 0.3s"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.2)";
              e.currentTarget.style.border = "1px solid rgba(255,255,255,0.5)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.border = "1px solid rgba(255,255,255,0.3)";
            }}
          >
            ›
          </button>
        </div>
      )}

      {/* Add CSS styles inline or in your global CSS */}
      <style jsx="true">{`
        .gallery-plan-section {
          text-align: center;
          background-color: #fff;
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .gallery-image-lists {
          grid-template-columns: repeat(3, 1fr);
          justify-content: center;
          align-items: center;
          gap: 15px;
          padding: 20px;
          display: grid;
        }
        
        @media (max-width: 1024px) {
          .gallery-image-lists {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        @media (max-width: 768px) {
          .gallery-image-lists {
            grid-template-columns: repeat(1, 1fr);
          }
          
          .gallery-plan-section {
            padding: 15px;
          }
        }
        
        @media (max-width: 480px) {
          .gallery-image-lists {
            gap: 10px;
            padding: 10px;
          }
        }
      `}</style>
    </>
  );
};

export default ProjectGallery;